import {
  ConfirmDialog,
  DataTable,
  type DataTableColumn,
  EmptyState,
  formatDateOnly,
  formatNumber,
  LoadingState,
  PageLayout,
  SearchInput,
} from '@repo/ui'
import { createFileRoute } from '@tanstack/react-router'
import { MegaphoneIcon, PlusIcon } from 'lucide-react'
import { useMemo, useState } from 'react'

import { PopupFormDialog } from '@/components/promo-popups/popup-form-dialog'
import { type BadgeTone, ToneBadge } from '@/components/status-badge'
import { Button } from '@/components/ui/button'
import { useDeletePromoPopup, usePromoPopups } from '@/hooks/use-promo-popups'
import type { PromoPopup } from '@/lib/api-types'

export const Route = createFileRoute('/_app/popups')({
  component: PromoPopupsPage,
})

const ROLE_LABELS: Record<string, string> = {
  OWNER: 'Proprietário',
  ADMIN: 'Admin',
  MANAGER: 'Gestor',
  AGENT: 'Atendente',
}

type PopupStatus = 'active' | 'inactive' | 'scheduled' | 'expired'

function resolveStatus(popup: PromoPopup): PopupStatus {
  if (!popup.isActive) return 'inactive'
  const now = Date.now()
  if (popup.startsAt && new Date(popup.startsAt).getTime() > now) {
    return 'scheduled'
  }
  if (popup.endsAt && new Date(popup.endsAt).getTime() < now) return 'expired'
  return 'active'
}

// Estados derivados da janela de exibição, mapeados para os tons semânticos
// compartilhados (mesma paleta do StatusBadge de assinatura).
const STATUS_META: Record<PopupStatus, { label: string; tone: BadgeTone }> = {
  active: { label: 'Ativo', tone: 'success' },
  inactive: { label: 'Inativo', tone: 'neutral' },
  scheduled: { label: 'Agendado', tone: 'info' },
  expired: { label: 'Expirado', tone: 'warning' },
}

function PopupStatusBadge({ popup }: { popup: PromoPopup }) {
  const { label, tone } = STATUS_META[resolveStatus(popup)]
  return <ToneBadge label={label} tone={tone} />
}

function PromoPopupsPage() {
  const popups = usePromoPopups()
  const remove = useDeletePromoPopup()
  const [search, setSearch] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<PromoPopup | null>(null)

  const rows = useMemo(() => {
    const normalized = search.trim().toLowerCase()
    const source = popups.data ?? []
    if (!normalized) return source
    return source.filter((popup) =>
      [popup.title, popup.body].join(' ').toLowerCase().includes(normalized)
    )
  }, [popups.data, search])

  function openCreate() {
    setEditing(null)
    setDialogOpen(true)
  }

  function openEdit(popup: PromoPopup) {
    setEditing(popup)
    setDialogOpen(true)
  }

  const columns: Array<DataTableColumn<PromoPopup>> = [
    {
      key: 'title',
      header: 'Popup',
      render: (popup) => (
        <div className="min-w-0">
          <p className="truncate font-medium">{popup.title}</p>
          <p className="text-muted-foreground line-clamp-1 text-xs">
            {popup.body}
          </p>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (popup) => <PopupStatusBadge popup={popup} />,
    },
    {
      key: 'audience',
      header: 'Público',
      render: (popup) => (
        <div className="text-xs">
          <p>
            {popup.audience === 'ALL'
              ? 'Todas as orgs'
              : `${popup.organizations.length} org(s)`}
          </p>
          <p className="text-muted-foreground">
            {popup.roles.length === 0
              ? 'Todos os papéis'
              : popup.roles.map((role) => ROLE_LABELS[role] ?? role).join(', ')}
          </p>
        </div>
      ),
    },
    {
      key: 'routes',
      header: 'Rotas',
      render: (popup) => (
        <span className="text-xs">
          {popup.routePatterns.length === 0
            ? 'Todas'
            : popup.routePatterns.join(', ')}
        </span>
      ),
    },
    {
      key: 'window',
      header: 'Janela',
      render: (popup) => (
        <span className="text-xs">
          {formatDateOnly(popup.startsAt)} - {formatDateOnly(popup.endsAt)}
        </span>
      ),
    },
    {
      key: 'priority',
      header: 'Prioridade',
      render: (popup) => popup.priority,
    },
    {
      key: 'metrics',
      header: 'Métricas',
      render: (popup) => (
        <span className="text-xs">
          {formatNumber(popup.impressionCount)} exib. /{' '}
          {formatNumber(popup.clickCount)} cliques
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (popup) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={() => openEdit(popup)}>
            Editar
          </Button>
          <ConfirmDialog
            title={`Remover o popup "${popup.title}"?`}
            confirmLabel="Excluir"
            destructive
            onConfirm={() => remove.mutate(popup.id)}
            trigger={
              <Button size="sm" variant="ghost" disabled={remove.isPending}>
                Excluir
              </Button>
            }
          />
        </div>
      ),
    },
  ]

  const hasPopups = (popups.data?.length ?? 0) > 0

  return (
    <PageLayout
      icon={MegaphoneIcon}
      title="Popups"
      description="Avisos promocionais exibidos para os usuários do app."
      actions={
        <Button onClick={openCreate}>
          <PlusIcon className="size-4" />
          Novo popup
        </Button>
      }
      contentClassName="space-y-4"
    >
      {popups.isLoading ? (
        <LoadingState />
      ) : !hasPopups ? (
        <EmptyState
          icon={MegaphoneIcon}
          title="Nenhum popup ainda"
          description="Crie um aviso para divulgar novidades, promoções ou manutenções no app."
          action={
            <Button onClick={openCreate}>
              <PlusIcon className="size-4" />
              Criar popup
            </Button>
          }
        />
      ) : (
        <>
          <SearchInput
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por título ou texto"
          />
          <DataTable
            rows={rows}
            columns={columns}
            getRowKey={(popup) => popup.id}
            empty="Nenhum popup encontrado para essa busca."
          />
        </>
      )}

      <PopupFormDialog
        open={dialogOpen}
        popup={editing}
        onOpenChange={setDialogOpen}
      />
    </PageLayout>
  )
}
